import { type IAdvancedRichText } from '../../inputs/rich-text-advanced/IAdvancedRichText.ts';
import {
  type IWidgetOption,
  type IWidgetOptionValue,
  isOptionValue,
} from './Widget.ts';

/**
 * Contenitore delle opzioni parsate da utilizzare nei componenti select/radio-group/checkbox-group specializzati.
 */
export type IComponentOptions =
  | IGroupedComponentOptions
  | IFlatComponentOptions;

/**
 * Contenitore di opzioni suddivise in gruppi
 */
export interface IGroupedComponentOptions {
  type: 'grouped';
  options: IGroupedSelectComponentOption[];
}

/**
 * Contenitore di opzioni "flat", ovvero come array di label-value
 */
export interface IFlatComponentOptions {
  type: 'flat';
  values: IRuntimeWidgetOptionValue[];
}

export interface IRuntimeWidgetOptionValue extends IWidgetOptionValue {
  disabled?: boolean;
}

/**
 * Struttura di un singolo gruppo di opzioni, label è il titolo del gruppo, options è l'array label-value delle singole opzioni
 */
export interface IGroupedSelectComponentOption {
  label: IAdvancedRichText;
  options: IRuntimeWidgetOptionValue[];
}

export class WidgetOptionLogic {
  static parseComponentOptions(options: IWidgetOption[]): IComponentOptions {
    // Se non ci sono divisori, è tutto un unico gruppo
    if (!options.some(option => option.type === 'divider')) {
      return {
        type: 'flat',
        values: options.filter(isOptionValue),
      };
    }

    // Altrimenti li divido in gruppi
    const result: IGroupedComponentOptions = {
      type: 'grouped',
      options: [],
    };
    let currentGroup: IGroupedSelectComponentOption;

    // Se il primo è un divider creo il suo gruppo, altrimenti ne creo "fantasma" uno senza titolo
    const isFirstDivider = options[0].type === 'divider';
    const start = isFirstDivider ? 1 : 0;
    currentGroup = {
      label: isFirstDivider ? options[0].label : [{ type: 'text', text: '' }],
      options: [],
    };

    // Per ogni elemento successivo, se è un divider creo un nuovo gruppo, altrimenti lo aggiungo al gruppo corrente
    for (let i = start; i < options.length; ++i) {
      const item = options[i];
      if (item.type === 'divider') {
        result.options.push(currentGroup);
        currentGroup = {
          label: item.label,
          options: [],
        };
      } else {
        currentGroup.options?.push(item);
      }
    }

    // Aggiungo al risultato l'ultimo gruppo creato
    result.options.push(currentGroup);

    return result;
  }
}
